// LineItemTable.tsx
import React, { useMemo, useState } from 'react';
import DataTable from 'react-data-table-component';
import Pagination from 'src/components/Common/Pagination';
import { formatUSD } from 'src/helpers/commonHelper';
import useIsMobile from 'src/hooks/useIsMobile';

export interface LineItem {
    line_item_name: string;
    start_date: string;
    end_date: string;
    media_type: string;
    creative_size: string;
    book_type: string;
    type_value: number;
    define_action: string;
    custom_note: string;
    geo_loc: any;
    geo_type: string;
    gross_total_budget_c: string;
    amount: number;
    kpi_ctr: number;
    kpi_cpc: number;
    kpi_cpa: number;
    kpi_view: number;
    cpm: string;
    management_fee: number;
    impressions: number;
    media_spend: number;
    spend_percentage_c: number;
    exchange: number;
    geo_name: string;
}

interface Props {
    lineitemInfo: LineItem[];
    creativeSizes: any;
    searchTerm: string;
}

const LineItemTable: React.FC<Props> = ({ lineitemInfo, creativeSizes, searchTerm }) => {

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const renderKPIs = (row: any) => {
        const formatCurrency = (value: number) =>
            new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);

        const hasKPI =
            row.kpi_ctr || row.kpi_view || row.kpi_cpc || row.kpi_cpa;

        if (!hasKPI) return <div>-</div>;

        return (
            <div>
                {row.kpi_ctr && <div>{Number(row.kpi_ctr).toFixed(2)}% CTR</div>}
                {row.kpi_view && <div>{Number(row.kpi_view).toFixed(2)}% Completion Rate</div>}
                {row.kpi_cpc && <div>{formatCurrency(row.kpi_cpc)} CPC</div>}
                {row.kpi_cpa && <div>{formatCurrency(row.kpi_cpa)} CPA</div>}
            </div>
        );
    };



    const renderGeoLocation = (row: any, maxLength: number = 40) => {
        let rawGeoName = row.geo_name || "";

        // Clean: remove quotes, unescape slashes, decode URI
        let geoName = decodeURIComponent(rawGeoName.replace(/^"|"$/g, "").replace(/\\\//g, "/"));

        // Truncate if too long
        const shortText = geoName.length > maxLength ? geoName.substring(0, maxLength) + "..." : geoName;

        // Ensure HTTPS
        if (geoName.startsWith("http://")) {
            geoName = geoName.replace("http://", "https://");
        }

        // Attachments (zip/custom)
        if (row.geo_type === "Zip Code Attachment" || row.geo_type === "Custom Attachment") {
            return (
                <span>
                    <a title={geoName} href={geoName || "#"} target="_blank" rel="noreferrer">
                        <i className="fas fa-paperclip"></i>
                    </a>
                </span>
            );
        }

        // Regular text
        return (
            <div
                style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: "100%",
                }}
                title={geoName} // full name on hover
            >
                {shortText || "-"} {/* Show '-' if geoName is empty */}
            </div>
        );
    };



    const renderTruncatedText = (text: string | null | undefined, maxLength: number = 40) => {
        if (!text) return <div>-</div>; // fallback for empty/null text

        const shortText = text.length > maxLength ? text.substring(0, maxLength) + "..." : text;

        return (
            <div
                style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: "100%",
                }}
                title={text} // full text on hover
            >
                {shortText}
            </div>
        );
    };



    const columns = [
        {
            name: "Line Item Name",
            selector: (row: LineItem) => row.line_item_name,
            cell: (row: LineItem) => {
                const fullText = row.line_item_name;
                const shortText = fullText.length > 40 ? fullText.substring(0, 40) + '...' : fullText;
                return (
                    <div
                        style={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: '100%',
                        }}
                        title={fullText}
                    >
                        {shortText}
                    </div>
                );
            },
            sortable: true,
            width: '150px',
        },
        {
            name: "Start Date",
            selector: (row: LineItem) => row.start_date,
            sortable: true
        },
        {
            name: "End Date",
            selector: (row: LineItem) => row.end_date,
            sortable: true
        },
        {
            name: "Media Type",
            selector: (row: LineItem) => row.media_type,
            cell: (row: LineItem) => (
                <div style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                    {row.media_type}
                </div>
            ),
            sortable: true
        },
        {
            name: "Creative Sizes",
            cell: (row: LineItem) => {
                let creativeList = '';
                try {
                    const creative = JSON.parse(row.creative_size); // decode JSON
                    const creativeMap = creative.reduce((acc: any, val: string) => {
                        acc[val] = true;
                        return acc;
                    }, {});
                    const sizes = creativeSizes[row.exchange] || {};
                    const intersectedKeys = Object.keys(sizes).filter(key => creativeMap[key]);
                    creativeList = intersectedKeys.map(key => sizes[key]).join(', ');
                } catch (error) {
                    creativeList = '';
                }

                const shortText = creativeList.length > 40 ? creativeList.substring(0, 40) + '...' : creativeList;

                return (
                    <div
                        style={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: '100%',
                        }}
                        title={creativeList}
                    >
                        {shortText}
                    </div>
                );
            },
            sortable: false,
            width: '250px',
        },
        {
            name: "Rate Type",
            selector: (row: LineItem) =>
                row.define_action
                    ? `${row.book_type} ( ${row.define_action} )`
                    : row.book_type,
            cell: (row: LineItem) => {
                const fullText = row.define_action
                    ? `${row.book_type} ( ${row.define_action} )`
                    : row.book_type;
                return (
                    <div
                        style={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: '100%',
                        }}
                        title={fullText}
                    >
                        {fullText}
                    </div>
                );
            },
            sortable: true
        },
        {
            name: "Rate",
            selector: (row: LineItem) =>
                row.cpm === 'percentage_fee'
                    ? Number(row.spend_percentage_c)
                    : Number(row.type_value), // 🔥 numeric value for sorting
            cell: (row: LineItem) => {
                const value =
                    row.cpm === 'percentage_fee'
                        ? `${Number(row.spend_percentage_c).toFixed(2)}%`
                        : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(row.type_value);

                return (
                    <div style={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: '130px' // keep it within column width
                    }}>
                        {value}
                    </div>
                );
            },
            sortable: true
        },
        {
            name: "Booked Units",
            selector: (row: LineItem) => {
                // Return raw value for sorting
                return row.cpm === 'percentage_fee'
                    ? Number(row.media_spend)
                    : Number(row.impressions || 0);
            },
            cell: (row: LineItem) => {
                let value = row.cpm === 'percentage_fee'
                    ? Number(row.media_spend)
                    : Number(row.impressions || 0);

                // Format for display
                const formattedValue = row.cpm === 'percentage_fee'
                    ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value)
                    : value.toLocaleString();

                return (
                    <div
                        title={formattedValue}
                        style={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: '140px',
                            display: 'inline-block',
                            verticalAlign: 'middle',
                            cursor: 'default',
                        }}
                    >
                        {formattedValue}
                    </div>
                );
            },
            sortable: true,
            width: '150px',
        },
        {
            name: 'KPI',
            selector: (row: LineItem) => {
                // Get the first available numeric KPI value
                return Number(row.kpi_ctr) || Number(row.kpi_view) || Number(row.kpi_cpc) || Number(row.kpi_cpa) || 0;
            },
            cell: (row: LineItem) => renderKPIs(row),
            sortable: true
        },
        {
            name: "Custom Notes",
            selector: (row: LineItem) => row.custom_note,
            cell: (row: LineItem) => renderTruncatedText(row.custom_note, 40),
            sortable: true
        },
        {
            name: "Geo Type",
            selector: (row: LineItem) => row.geo_type,
            cell: (row: LineItem) => (
                <div
                    style={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: '100%',
                    }}
                    title={row.geo_type}
                >
                    {row.geo_type}
                </div>
            ),
            sortable: true
        },
        {
            name: "Geo Location",
            selector: (row: LineItem) => {
                let rawGeoName = row.geo_name || "";
                return decodeURIComponent(
                    rawGeoName.replace(/^"|"$/g, '').replace(/\\\//g, '/')
                );
            },
            cell: (row: LineItem) => renderGeoLocation(row, 40),
            sortable: true        },
        {
            name: "Amount",
            selector: (row: LineItem) => Number(row.amount),
            cell: (row: LineItem) => {

                return (
                    <div
                        title={formatUSD(row.amount)} // Tooltip shows full amount
                        style={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: '140px',         // Must be fixed
                            display: 'inline-block',  // Ensures ellipsis works
                            verticalAlign: 'middle',
                            cursor: 'default'         // Optional: show pointer or default
                        }}
                    >
                        {formatUSD(row.amount)}
                    </div>
                );
            },
            sortable: true
        },
    ];

    const customStyles = {
        table: {
            style: {
                border: '1px solid #ddd',
            },
        },
        tableWrapper: {
            style: {
                borderBottom: '1px solid #ced4da61',
            },
        },
        rows: {
            style: {
                borderBottom: '1px solid #ddd',
            },
        },
        headCells: {
            style: {
                borderBottom: '2px solid #ddd',
                fontWeight: 'bold',
                borderRight: '1px solid #ddd',
            },
        },
        cells: {
            style: {
                borderBottom: '1px solid #ddd',
                borderRight: '1px solid #ddd',
            },
        },
        pagination: {
            style: {
                borderTop: '1px solid #ced4da61',
            },
        },
    };

    // Filter by search term
    const filteredData = useMemo(() => {
        return lineitemInfo.filter(item =>
            Object.values(item).some(value =>
                String(value).toLowerCase().includes(searchTerm.toLowerCase())
            )
        );
    }, [lineitemInfo, searchTerm]);

    // Total pages
    const totalPages = Math.ceil(filteredData.length / pageSize);

    // Slice for current page
    const currentData = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        return filteredData.slice(start, start + pageSize);
    }, [filteredData, currentPage, pageSize]);

    const isMobile = useIsMobile();



    return (
        <>
            {!isMobile ?
                <DataTable
                    className="data-table"
                    columns={columns}
                    data={currentData}
                    customStyles={customStyles}
                />
                :
                <div className="d-block d-md-none mobile-cards-container">
                    {currentData.map((lineItem: any) => {
                        return (
                            <div className="mobile-card card " key={lineItem.id}>
                                <div
                                    className="mobile-card-row"
                                >
                                    <span className="row-title fw-bold">
                                        Line Item Name
                                    </span>
                                    <span className="row-value">
                                        {lineItem.line_item_name}
                                    </span>
                                </div>
                                <div
                                    className="mobile-card-row "
                                >
                                    <span className="row-title fw-bold">
                                        Start Date
                                    </span>
                                    <span className="row-value">
                                        {lineItem.start_date}
                                    </span>
                                </div>
                                <div
                                    className="mobile-card-row "
                                >
                                    <span className="row-title fw-bold">
                                        End Date
                                    </span>
                                    <span className="row-value">
                                        {lineItem.end_date}
                                    </span>
                                </div>
                                <div
                                    className="mobile-card-row "
                                >
                                    <span className="row-title fw-bold">
                                        Media Type
                                    </span>
                                    <span className="row-value">
                                        {lineItem.media_type}
                                    </span>
                                </div>
                                <div className="mobile-card-row">
                                    <span className="row-title fw-bold">
                                        Creative Sizes
                                    </span>
                                    <span
                                        className="row-value"
                                        style={{
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            maxWidth: "100%", // keeps it ellipsed nicely
                                            display: "inline-block",
                                        }}
                                        title={(() => {
                                            try {
                                                const creative = JSON.parse(lineItem.creative_size);
                                                const creativeMap = creative.reduce((acc: any, val: string) => {
                                                    acc[val] = true;
                                                    return acc;
                                                }, {});
                                                const sizes = creativeSizes[lineItem.exchange] || {};
                                                const intersectedKeys = Object.keys(sizes).filter(key => creativeMap[key]);
                                                return intersectedKeys.map(key => sizes[key]).join(", ");
                                            } catch {
                                                return "";
                                            }
                                        })()}
                                    >
                                        {(() => {
                                            try {
                                                const creative = JSON.parse(lineItem.creative_size);
                                                const creativeMap = creative.reduce((acc: any, val: string) => {
                                                    acc[val] = true;
                                                    return acc;
                                                }, {});
                                                const sizes = creativeSizes[lineItem.exchange] || {};
                                                const intersectedKeys = Object.keys(sizes).filter(key => creativeMap[key]);
                                                const creativeList = intersectedKeys.map(key => sizes[key]).join(", ");
                                                return creativeList.length > 40
                                                    ? creativeList.substring(0, 40) + "..."
                                                    : creativeList;
                                            } catch {
                                                return "";
                                            }
                                        })()}
                                    </span>
                                </div>
                                <div
                                    className="mobile-card-row "
                                >
                                    <span className="row-title fw-bold">
                                        Rate Type
                                    </span>
                                    <span className="row-value">
                                        {lineItem.book_type}
                                    </span>
                                </div>
                                <div
                                    className="mobile-card-row "
                                >
                                    <span className="row-title fw-bold">
                                        Rate
                                    </span>
                                    <span className="row-value">
                                        {lineItem.type_value}
                                    </span>
                                </div>
                                <div className="mobile-card-row">
                                    <span className="row-title fw-bold">Booked Units</span>
                                    <span className="row-value">
                                        {(() => {
                                            let value = lineItem.cpm === "percentage_fee"
                                                ? Number(lineItem.media_spend)
                                                : Number(lineItem.impressions || 0);

                                            // Format for display
                                            const formattedValue =
                                                lineItem.cpm === "percentage_fee"
                                                    ? new Intl.NumberFormat("en-US", {
                                                        style: "currency",
                                                        currency: "USD",
                                                    }).format(value)
                                                    : value.toLocaleString();

                                            return (
                                                <div
                                                    title={formattedValue}
                                                    style={{
                                                        whiteSpace: "nowrap",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        maxWidth: "140px",
                                                        display: "inline-block",
                                                        verticalAlign: "middle",
                                                        cursor: "default",
                                                    }}
                                                >
                                                    {formattedValue}
                                                </div>
                                            );
                                        })()}
                                    </span>
                                </div>
                                <div
                                    className="mobile-card-row "
                                >
                                    <span className="row-title fw-bold">
                                        KPIS
                                    </span>
                                    <span className="row-value">
                                        {renderKPIs(lineItem)}
                                    </span>
                                </div>
                                <div
                                    className="mobile-card-row "
                                >
                                    <span className="row-title fw-bold">
                                        Custom Notes
                                    </span>
                                    <span className="row-value">
                                        {renderTruncatedText(lineItem.custom_note, 40)}
                                    </span>
                                </div>
                                <div
                                    className="mobile-card-row "
                                >
                                    <span className="row-title fw-bold">
                                        Geo Type
                                    </span>
                                    <span className="row-value">
                                        {lineItem.geo_type}
                                    </span>
                                </div>
                                <div
                                    className="mobile-card-row "
                                >
                                    <span className="row-title fw-bold">
                                        Geo Location
                                    </span>
                                    <span className="row-value">
                                        {renderGeoLocation(lineItem, 40)}
                                    </span>
                                </div>
                                <div
                                    className="mobile-card-row "
                                >
                                    <span className="row-title fw-bold">
                                        Amount
                                    </span>
                                    <span className="row-value">
                                        {formatUSD(lineItem.amount)}
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            }
            {/* Pagination  */}
            <Pagination
                totalPages={totalPages}
                currentPage={currentPage}
                onPageChange={setCurrentPage}
                currentCount={currentData.length}
                pageSize={pageSize}
                onPageSizeChange={(size) => {
                    setPageSize(size);
                    setCurrentPage(1); // reset page when page size changes
                }}
            />
        </>
    );
};

export default LineItemTable;
